import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { firstPageParams, list, type Column } from "../../ui/list";
import { openModal } from "../../ui/modal";
import { field, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";
import type { PageInfo } from "../../../server/rules/list-page";
import { patientRecord } from "./record";

// Home Care › Patients (brief 1C-1, mockup homecare/cases): every patient with their client type,
// the referral and the stage of their episode (from its workflow), as a list or a board by stage,
// with stage chips, search and New referral. A row opens the patient.

export interface PatientRow {
  id: string;
  name: string;
  clientType: string;
  client: string;
  referral: string;
  episode: number | null;
  stage: string;
  /** "No county yet", for someone scoped by county (brief 1D-6): shown, never hidden. */
  noCounty?: string | null;
}
export interface PatientList extends PageInfo {
  patients: PatientRow[];
  stages: string[];
  newest: string | null;
  inIntake: number;
  canEdit: boolean;
}

/** The mockup's stage chip: the first stage plain, the last done, the rest in progress. */
export function patientStageChip(stages: readonly string[], name: string): HTMLElement {
  const i = stages.indexOf(name);
  const kind = i === stages.length - 1 ? "ok" : i <= 0 ? "plain" : "info";
  return el("span", { class: `chip ${kind}` }, name || "–");
}

/** Asks the server for one page of patients (brief 1E-32). */
export const askPatients = (params: URLSearchParams) =>
  api<PatientList>("GET", `/api/patients?${params.toString()}`);

export async function patientsScreen(host: HTMLElement, ctx: ScreenContext) {
  if (ctx.record) return patientRecord(host, ctx, ctx.record);
  const listId = `${ctx.section.id}/patients`;
  const res = await askPatients(firstPageParams(listId, ctx.me.user.id));
  const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " New referral");
  add.addEventListener("click", () => newReferral(ctx, { source: "manual" }));
  host.append(
    pageHead(
      ctx,
      "Each stay is an episode of care, from admission to discharge.",
      res.ok && res.body.canEdit ? [add] : [],
    ),
  );
  if (!res.ok) {
    host.append(el("p", { class: "note", role: "alert" }, "The patients couldn't be loaded."));
    return;
  }
  host.append(patientList(ctx, res.body, listId));
}

/** The list (and board) of patients by stage, as Patients and the Home Care overview show it. */
export function patientList(ctx: ScreenContext, first: PatientList, id: string): HTMLElement {
  const stages = first.stages;
  const columns: Column<PatientRow>[] = [
    {
      id: "name",
      label: "Patient",
      text: (r) => r.name,
      search: true,
      cell: (r) =>
        r.noCounty
          ? el("span", {}, el("b", {}, r.name), " ", el("span", { class: "chip warn" }, r.noCounty))
          : el("b", {}, r.name),
    },
    { id: "type", label: "Client type", text: (r) => r.clientType, search: true },
    {
      id: "referral",
      label: "Referral",
      text: (r) => `${r.client} · ${r.referral}`,
      search: true,
    },
    {
      id: "stage",
      label: "Stage",
      text: (r) => r.stage,
      cell: (r) => patientStageChip(stages, r.stage),
      values: stages,
    },
  ];
  return list<PatientRow>({
    id,
    userId: ctx.me.user.id,
    noun: "patients",
    searchLabel: "Search patients, clients or types",
    first: { rows: first.patients, page: first },
    source: async (params) => {
      const r = await askPatients(params);
      return r.ok ? { rows: r.body.patients, page: r.body } : null;
    },
    rowId: (r) => r.id,
    columns,
    chips: { values: stages, of: (r) => r.stage },
    board: {
      stages,
      stageOf: (r) => r.stage,
      tile: (r) => [r.name, r.clientType, r.client],
    },
    onOpen: (r) => ctx.go(`/homecare/patients/${r.id}`),
    empty: "No patients yet. New referral starts one.",
  });
}

// ---- New referral (and Start intake from the inbox or a scan) -------------------------------------

export interface ReferralStart {
  source: "manual" | "inbox" | "scan" | "call";
  clientId?: string | null;
  inboxItemId?: string;
  scanFileId?: string;
  /** Words to start from, e.g. the sender's name. */
  hint?: string;
  /** After the patient is made (the inbox item is done). */
  done?: () => void;
}

/**
 * New referral, in a modal: the client it's for, how it came in, the patient's name, date of birth
 * and phone. The duplicate check runs as the name and date of birth are typed, and again on save:
 * a patient who may be the same is offered first ("open them instead").
 */
export async function newReferral(ctx: ScreenContext, start: ReferralStart) {
  const choices = await api<{
    clients: { id: string; name: string; type: string | null }[];
    sources: { value: string; label: string }[];
  }>("GET", "/api/patients/clients");
  if (!choices.ok) return toast("Referrals can't be started", choices.body.error);
  const client = select(
    "Client",
    [
      ["", "Pick the client"],
      ...choices.body.clients.map(
        (c) => [c.id, c.type ? `${c.name} · ${c.type}` : `${c.name} · no type yet`] as const,
      ),
    ],
    start.clientId ?? "",
  );
  const source = select(
    "How it came in",
    choices.body.sources
      .filter((s) => (start.source === "manual" ? !["inbox", "scan"].includes(s.value) : true))
      .map((s) => [s.value, s.label] as const),
    start.source,
  );
  if (start.source === "inbox" || start.source === "scan") source.input.disabled = true;
  const first = field("Patient first name", "", { maxlength: "60", autocomplete: "off" });
  const last = field("Patient last name", "", { maxlength: "60", autocomplete: "off" });
  const born = field("Date of birth", "", { type: "date" });
  const phone = field("Phone", "", { type: "tel", maxlength: "20", inputmode: "tel" });
  const dup = el("div", { "aria-live": "polite" });
  const create = el(
    "button",
    { type: "submit", class: "btn pri" },
    icon("person-add"),
    start.source === "manual" || start.source === "call" ? " Add referral" : " Start intake",
  );
  const form = el(
    "form",
    { class: "form", style: "max-width:none", novalidate: "" },
    el(
      "div",
      { class: "formgrid" },
      el("div", { class: "wide" }, client.el),
      source.el,
      el("div"),
      first.el,
      last.el,
      born.el,
      phone.el,
    ),
    dup,
  );
  const m = openModal({
    title: start.source === "manual" || start.source === "call" ? "New referral" : "Start intake",
    sub: start.hint ?? "The client's type decides what intake asks and what the payer needs.",
    body: [form],
    actions: [create],
    wide: true,
  });
  (start.clientId ? first.input : client.input).focus();

  let notDuplicate = false;
  const showDuplicates = (ds: { id: string; name: string; born: string; words: string }[]) => {
    notDuplicate = false;
    if (!ds.length) return dup.replaceChildren();
    dup.replaceChildren(
      el(
        "div",
        { class: "note warnnote", role: "status" },
        ...ds.map((d) => {
          const open = el("button", { type: "button", class: "btn sm" }, icon("eye"), " Open");
          open.setAttribute("aria-label", `Open ${d.name}`);
          open.addEventListener("click", () => {
            m.close();
            ctx.go(`/homecare/patients/${d.id}`);
          });
          return el("p", {}, d.words, " ", open);
        }),
        (() => {
          const anyway = el(
            "button",
            { type: "button", class: "btn sm ghost" },
            "It's someone else: add a new patient",
          );
          anyway.addEventListener("click", () => {
            notDuplicate = true;
            anyway.replaceWith(el("span", { class: "small muted" }, "A new patient will be made."));
          });
          return el("p", {}, anyway);
        })(),
      ),
    );
  };
  let n = 0;
  const check = async () => {
    const mine = ++n;
    if (!first.input.value.trim() || !last.input.value.trim() || !born.input.value) return;
    const res = await api<{
      duplicates: { id: string; name: string; born: string; words: string }[];
    }>("POST", "/api/patients/check", {
      firstName: first.input.value,
      lastName: last.input.value,
      birthDate: born.input.value,
    });
    if (mine === n && res.ok) showDuplicates(res.body.duplicates);
  };
  for (const i of [first.input, last.input, born.input]) i.addEventListener("change", check);

  const submit = async () => {
    create.disabled = true;
    const res = await api<{
      patient: { id: string; name: string };
      duplicates?: { id: string; name: string; born: string; words: string }[];
    }>("POST", "/api/referrals", {
      clientId: client.input.value,
      source: start.source === "manual" ? source.input.value : start.source,
      firstName: first.input.value,
      lastName: last.input.value,
      birthDate: born.input.value || null,
      phone: phone.input.value || null,
      inboxItemId: start.inboxItemId,
      scanFileId: start.scanFileId,
      notDuplicate,
    });
    create.disabled = false;
    if (res.status === 409 && res.body.duplicates) return showDuplicates(res.body.duplicates);
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    toast(
      start.source === "inbox" || start.source === "scan" ? "Intake started" : "Referral added",
      `${res.body.patient.name} · answer intake next`,
    );
    start.done?.();
    ctx.go(`/homecare/patients/${res.body.patient.id}`);
  };
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    void submit();
  });
  create.addEventListener("click", (e) => {
    e.preventDefault();
    void submit();
  });
}
